import { useState, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Trash2, ChevronUp, ChevronDown, Truck, Loader2 } from 'lucide-react';
import { api } from '@/api/client';

type OrderType = 'B2C' | 'B2B';

interface Courier {
  id: number;
  name: string;
  code: string;
  brand?: string;
  serviceType: string;
  orderType: OrderType;
  isActive: boolean;
}

interface MerchantCourier {
  id: number;
  courierId: number;
  priority: number;
  isActive: boolean;
  isDisplay: boolean;
  courier?: Courier;
}

export function MerchantCouriersTab({ merchantId }: { merchantId: number }) {
  const [tab, setTab] = useState<OrderType>('B2C');
  const qc = useQueryClient();

  const couriersQ = useQuery({
    queryKey: ['couriers', 'all'],
    queryFn: async () => (await api.get<Courier[]>('/couriers', { params: { isActive: 'true' } })).data,
    staleTime: 1000 * 60 * 5,
  });

  const assignedQ = useQuery({
    queryKey: ['merchant-couriers', merchantId],
    queryFn: async () => (await api.get<MerchantCourier[]>(`/merchants/${merchantId}/couriers`)).data,
  });

  const assign = useMutation({
    mutationFn: async (body: { courierId: number; priority: number }) => (await api.post(`/merchants/${merchantId}/couriers`, body)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['merchant-couriers', merchantId] }); toast.success('Courier assigned'); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to assign'),
  });

  const unassign = useMutation({
    mutationFn: async (courierId: number) => (await api.delete(`/merchants/${merchantId}/couriers/${courierId}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['merchant-couriers', merchantId] }); toast.success('Courier removed'); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to remove'),
  });

  const reorder = useMutation({
    mutationFn: async (ordering: { courierId: number; priority: number }[]) => (await api.patch(`/merchants/${merchantId}/couriers/reorder`, { ordering })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['merchant-couriers', merchantId] }); },
  });

  const allInTab = useMemo(
    () => (couriersQ.data ?? []).filter(c => c.orderType === tab),
    [couriersQ.data, tab],
  );

  const assignedInTab = useMemo(() => {
    const all = assignedQ.data ?? [];
    return all
      .filter(mc => mc.isActive && mc.courier?.orderType === tab)
      .sort((a, b) => a.priority - b.priority);
  }, [assignedQ.data, tab]);

  const unassignedInTab = useMemo(() => {
    const assignedIds = new Set(assignedInTab.map(mc => mc.courierId));
    return allInTab.filter(c => !assignedIds.has(c.id));
  }, [allInTab, assignedInTab]);

  const move = (idx: number, dir: -1 | 1) => {
    const j = idx + dir;
    if (j < 0 || j >= assignedInTab.length) return;
    const next = [...assignedInTab];
    [next[idx], next[j]] = [next[j], next[idx]];
    reorder.mutate(next.map((mc, i) => ({ courierId: mc.courierId, priority: (i + 1) * 10 })));
  };

  if (couriersQ.isLoading || assignedQ.isLoading) {
    return <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex gap-1 border border-slate-200 rounded-lg p-0.5 bg-slate-50">
          {(['B2C','B2B'] as const).map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-1.5 text-sm font-medium rounded-md transition ${tab === t ? 'bg-white shadow-sm text-primary' : 'text-slate-600 hover:text-slate-900'}`}>
              {t}
            </button>
          ))}
        </div>
        <p className="text-xs text-slate-500">
          Order = default routing priority used when no courier is selected on an order.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* Left: Available */}
        <Panel title={`Available ${tab} Couriers`} count={unassignedInTab.length}>
          {unassignedInTab.length === 0 ? (
            <Empty msg={`All ${tab} couriers already assigned.`} />
          ) : (
            <ul className="divide-y divide-slate-100">
              {unassignedInTab.map((c) => (
                <li key={c.id} className="flex items-center justify-between p-2.5 hover:bg-slate-50">
                  <div className="min-w-0">
                    <div className="font-medium text-slate-900 truncate">{c.name} <span className="text-xs text-slate-400 font-mono">#{c.id}</span></div>
                    <div className="text-xs text-slate-500"><code>{c.code}</code> · {c.serviceType}</div>
                  </div>
                  <button
                    onClick={() => assign.mutate({ courierId: c.id, priority: (assignedInTab.length + 1) * 10 })}
                    disabled={assign.isPending}
                    className="p-1.5 text-primary hover:bg-primary/10 rounded" title="Assign">
                    <Plus size={16} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        {/* Right: Assigned in priority */}
        <Panel title={`Assigned ${tab} (Priority)`} count={assignedInTab.length}>
          {assignedInTab.length === 0 ? (
            <Empty msg="No couriers assigned yet. Add from the left." />
          ) : (
            <ul className="divide-y divide-slate-100">
              {assignedInTab.map((mc, idx) => (
                <li key={mc.id} className="flex items-center justify-between p-2.5 hover:bg-slate-50">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center flex-shrink-0">{idx + 1}</span>
                    <div className="min-w-0">
                      <div className="font-medium text-slate-900 truncate">{mc.courier?.name} <span className="text-xs text-slate-400 font-mono">#{mc.courierId}</span></div>
                      <div className="text-xs text-slate-500"><code>{mc.courier?.code}</code> · {mc.courier?.serviceType}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5">
                    <button onClick={() => move(idx, -1)} disabled={idx === 0 || reorder.isPending} className="p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded disabled:opacity-30" title="Move up"><ChevronUp size={14} /></button>
                    <button onClick={() => move(idx, 1)} disabled={idx === assignedInTab.length - 1 || reorder.isPending} className="p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded disabled:opacity-30" title="Move down"><ChevronDown size={14} /></button>
                    <button onClick={() => unassign.mutate(mc.courierId)} disabled={unassign.isPending} className="p-1.5 text-slate-400 hover:text-danger hover:bg-danger/10 rounded ml-1" title="Remove"><Trash2 size={14} /></button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </div>
  );
}

function Panel({ title, count, children }: { title: string; count: number; children: React.ReactNode }) {
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">{title}</span>
        <span className="text-xs font-mono text-slate-400">{count}</span>
      </div>
      <div className="max-h-96 overflow-y-auto">{children}</div>
    </div>
  );
}

function Empty({ msg }: { msg: string }) {
  return (
    <div className="text-center py-8 text-slate-400">
      <Truck size={20} className="mx-auto mb-1 opacity-50" />
      <p className="text-xs">{msg}</p>
    </div>
  );
}
